// A session that has ENDED on the server — what the app does about it (BUGS.md S1, 24/10/2026).
//
// The server now checks every request again: somebody archived, made an employee, signed out by
// a password change, or simply seven days on, is refused with a 401 carrying the header
// `X-Session-Ended: 1` (backend `auth.SESSION_ENDED_HEADER`). Before this the app had no idea —
// it showed red "Could not validate credentials" messages on every click until a refresh.
//
// The rules (`sessionEnded.checks.mjs` pins each one):
//   1. ONLY a 401 WITH THE HEADER means the session is over. A wrong password or a wrong 2FA
//      code on the sign-in screens is a 401 too, and must leave the person where they are.
//   2. A support session (a superadmin acting as somebody, with consent) that ends goes back
//      to the superadmin's own session — not to the sign-in page.
//   3. Otherwise: forget the session and go to the sign-in page, which says why.
//   4. Many requests fail at once when a session ends (a page loads five things); the app
//      reacts to the first and ignores the rest.
//
// ⚠️ NO REACT AND NO IMPORTS: `sessionEnded.checks.mjs` imports this file under plain node.

export const SESSION_ENDED_HEADER = 'x-session-ended';
// The tab's own note to itself across the reload to /login (sessionStorage is per tab) —
// the same key `signInElsewhere.js` uses, so one place says why the page changed.
export const NOTICE_KEY = 'hh_signed_in_elsewhere';
export const ENDED = 'session_ended';
export const SUPPORT_ENDED = 'support_ended';
// Set in shared storage just before the session is forgotten, so OTHER tabs — which reload
// when the session disappears — say "signed out" for the right reason.
export const ENDED_AT_KEY = 'hh_session_ended_at';

/** Rule 1: is this failed request the server saying the session is over? */
export function isSessionEnded(error) {
  const response = error && error.response;
  if (!response || response.status !== 401) return false;
  const headers = response.headers || {};
  const value = typeof headers.get === 'function'
    ? headers.get(SESSION_ENDED_HEADER)
    : (headers[SESSION_ENDED_HEADER] ?? headers['X-Session-Ended']);
  return String(value) === '1';
}

/** Rules 2 and 3: what to do. */
export function whatNext({ supportToken }) {
  return supportToken ? SUPPORT_ENDED : ENDED;
}

/** What the page says once it has reloaded. */
export function noticeFor(kind) {
  if (kind === ENDED) return "You've been signed out. Please sign in again.";
  if (kind === SUPPORT_ENDED) return 'The support session has ended. You are back as yourself.';
  return '';
}

/** Another tab's session disappeared — was it ended by the server in the last minute? */
export function endedRecently(stamp, now = Date.now()) {
  const at = Number(stamp);
  return Number.isFinite(at) && at > 0 && now - at < 60 * 1000;
}

// Rule 4, and the doing. `storage` is { local, session }, `go` navigates — passed in so the
// checks can run without a browser.
let handled = false;

export function handleSessionEnded({ storage, go }) {
  if (handled) return false;
  handled = true;
  const kind = whatNext({ supportToken: storage.local.getItem('sa_token') });
  try { storage.session.setItem(NOTICE_KEY, kind); } catch { /* the move still happens */ }
  if (kind === SUPPORT_ENDED) {
    const sa = storage.local.getItem('sa_token');
    storage.local.removeItem('sa_token');
    storage.local.removeItem('impersonation');
    storage.local.setItem('token', sa);
    go('/');
    return true;
  }
  try { storage.local.setItem(ENDED_AT_KEY, String(Date.now())); } catch { /* fine */ }
  storage.local.removeItem('token');
  storage.local.removeItem('sa_token');
  storage.local.removeItem('impersonation');
  go('/login');
  return true;
}

/** For the checks only: let `handleSessionEnded` act again. */
export function _resetForChecks() {
  handled = false;
}
